import React, { useState, useCallback, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Card from "components/card";
import {
  MdSearch, MdMail, MdCheckCircle, MdAccessTime,
  MdClose, MdReply, MdSend, MdPerson, MdCalendarToday,
} from "react-icons/md";
import { getInquiryList, updateInquiryStatus, replyToInquiry } from "api/adapters/admin/admin";

function debounce(fn, delay) {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), delay); };
}

function StatusBadge({ status }) {
  const isPending = status === "Pending";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
      isPending
        ? "bg-orange-100 text-orange-600 dark:bg-orange-500/20 dark:text-orange-400"
        : "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400"
    }`}>
      <span className={`h-1.5 w-1.5 rounded-full ${isPending ? "bg-orange-500" : "bg-green-500"}`} />
      {status}
    </span>
  );
}

// ─── Reply Drawer ──────────────────────────────────────────────────────────────

function ReplyDrawer({ inquiry, onClose, onReplied }) {
  const [replyText, setReplyText] = useState("");
  const [sent, setSent] = useState(false);
  const textareaRef = useRef(null);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // Focus textarea when drawer opens
  useEffect(() => {
    if (textareaRef.current) setTimeout(() => textareaRef.current?.focus(), 80);
  }, []);

  const { mutate: sendReply, isPending } = useMutation({
    mutationFn: () => replyToInquiry(inquiry._id, replyText),
    onSuccess: () => {
      setSent(true);
      onReplied();
    },
  });

  const alreadyReplied = !!inquiry.sAdminReply;
  const canSend = replyText.trim().length >= 5;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 z-50 flex h-full w-full max-w-[480px] flex-col bg-white shadow-2xl dark:bg-navy-800">

        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 dark:border-white/10">
          <div className="flex items-center gap-2">
            <MdMail className="h-5 w-5 text-orange-500" />
            <h2 className="text-base font-bold text-navy-700 dark:text-white">Inquiry Detail</h2>
          </div>
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/10 dark:hover:text-white"
          >
            <MdClose className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">

          {/* User info */}
          <div className="mb-5 flex items-start gap-4 rounded-2xl bg-orange-50 p-4 dark:bg-orange-500/10">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-orange-500 text-lg font-bold text-white">
              {inquiry.sName?.charAt(0)?.toUpperCase() || "?"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-bold text-navy-700 dark:text-white">{inquiry.sName}</p>
                <StatusBadge status={inquiry.eStatus} />
              </div>
              <div className="mt-1 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <MdPerson className="h-3.5 w-3.5" />
                <span>{inquiry.sEmail}</span>
              </div>
              <div className="mt-0.5 flex items-center gap-1 text-xs text-gray-400">
                <MdCalendarToday className="h-3.5 w-3.5" />
                <span>{inquiry.dCreatedAt ? new Date(inquiry.dCreatedAt).toLocaleString() : "—"}</span>
              </div>
            </div>
          </div>

          {/* User's message */}
          <div className="mb-5">
            <p className="mb-2 text-xs font-bold uppercase tracking-wider text-orange-500">
              User&apos;s Message
            </p>
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 text-sm leading-relaxed text-gray-700 dark:border-white/10 dark:bg-navy-900 dark:text-gray-300">
              {inquiry.sMessage}
            </div>
          </div>

          {/* Previous reply (if already replied) */}
          {alreadyReplied && (
            <div className="mb-5">
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-green-500">
                Previous Reply Sent
              </p>
              <div className="rounded-xl border border-green-100 bg-green-50 p-4 text-sm leading-relaxed text-gray-700 dark:border-green-500/20 dark:bg-green-500/10 dark:text-gray-300">
                {inquiry.sAdminReply}
              </div>
              <p className="mt-1.5 text-xs text-gray-400">
                An email was sent to {inquiry.sEmail} with the above reply.
              </p>
            </div>
          )}

          {/* Reply section */}
          {sent ? (
            <div className="flex flex-col items-center py-6 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-100 dark:bg-green-500/20">
                <MdCheckCircle className="h-8 w-8 text-green-500" />
              </div>
              <p className="mt-4 text-base font-bold text-navy-700 dark:text-white">Reply sent!</p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                An email has been sent to {inquiry.sEmail}
              </p>
              <button
                onClick={onClose}
                className="mt-5 rounded-xl bg-orange-500 px-6 py-2.5 text-sm font-semibold text-white hover:bg-orange-600 transition-colors"
              >
                Done
              </button>
            </div>
          ) : (
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-orange-500">
                {alreadyReplied ? "Send a New Reply" : "Write Your Reply"}
              </p>
              <textarea
                ref={textareaRef}
                rows={5}
                placeholder="Type your reply here... The user will receive it by email."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                className="w-full resize-none rounded-xl border border-gray-200 bg-white p-4 text-sm leading-relaxed text-gray-700 outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-200 dark:border-white/10 dark:bg-navy-900 dark:text-gray-200 dark:focus:border-orange-500 dark:focus:ring-orange-500/20"
              />
              {replyText.trim().length > 0 && replyText.trim().length < 5 && (
                <p className="mt-1 text-xs text-red-500">Reply must be at least 5 characters.</p>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        {!sent && (
          <div className="border-t border-gray-100 px-6 py-4 dark:border-white/10">
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-500 hover:bg-gray-50 transition-colors dark:border-white/10 dark:text-gray-400 dark:hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                disabled={!canSend || isPending}
                onClick={() => sendReply()}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-orange-500 py-2.5 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <MdSend className="h-4 w-4" />
                {isPending ? "Sending…" : "Send Reply"}
              </button>
            </div>
            <p className="mt-2.5 text-center text-xs text-gray-400">
              Reply will be emailed to <span className="font-medium text-gray-500">{inquiry.sEmail}</span>
            </p>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

const ContactInquiries = () => {
  const queryClient = useQueryClient();
  const [sSearch, setSearch] = useState("");
  const [eStatus, setStatusFilter] = useState("");
  const [nPage, setPage] = useState(1);
  const [actionError, setActionError] = useState("");
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  const debouncedSearch = useCallback(
    debounce((val) => { setSearch(val); setPage(1); }, 400),
    []
  );

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["inquiryList", sSearch, eStatus, nPage],
    queryFn: () => getInquiryList({ sSearch, eStatus, nPage, nLimit: 10 }),
    keepPreviousData: true,
  });

  const list = data?.data;
  const inquiries = list?.aInquiries ?? [];
  const nTotalPages = list?.nTotalPages ?? 1;

  const { mutate: doUpdateStatus, isPending: updating } = useMutation({
    mutationFn: ({ id, eStatus: s }) => updateInquiryStatus(id, s),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inquiryList"] });
      setActionError("");
    },
    onError: (err) => setActionError(err.message || "Failed to update status."),
  });

  function handleReplied() {
    queryClient.invalidateQueries({ queryKey: ["inquiryList"] });
  }

  return (
    <div className="flex flex-col gap-5">
      <Card extra="p-5">

        {/* Filters */}
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <select
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="h-10 rounded-xl border border-gray-200 bg-white px-3 text-sm outline-none focus:border-orange-500 dark:border-white/10 dark:bg-[#111111] dark:text-white"
            >
              <option value="">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Closed">Closed</option>
            </select>
          </div>
          <div className="relative w-full sm:w-64">
            <MdSearch className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search inquiries..."
              onChange={(e) => debouncedSearch(e.target.value)}
              className="h-10 w-full rounded-xl border border-gray-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-200 dark:border-white/10 dark:bg-[#111111] dark:text-white"
            />
          </div>
        </div>

        {/* Error banner */}
        {actionError && (
          <div className="mb-4 flex items-center justify-between rounded-lg bg-red-50 px-4 py-2.5 dark:bg-red-500/10">
            <p className="text-sm font-medium text-red-600 dark:text-red-400">{actionError}</p>
            <button onClick={() => setActionError("")} className="ml-3 text-red-400 hover:text-red-600">
              <MdClose className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="border-b-2 border-orange-100 dark:border-white/10">
                {["User", "Message", "Status", "Date", "Actions"].map((h) => (
                  <th key={h} className="pb-3 pr-4 text-xs font-bold uppercase tracking-wider text-orange-500 dark:text-orange-400">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-50 dark:border-white/5">
                    {Array.from({ length: 5 }).map((__, j) => (
                      <td key={j} className="py-3 pr-4">
                        <div className="h-4 animate-pulse rounded bg-gray-100 dark:bg-[#1a1a1a]" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : inquiries.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-16 text-center">
                    <MdMail className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                    <p className="text-sm text-gray-400">No inquiries found.</p>
                  </td>
                </tr>
              ) : (
                inquiries.map((inq) => (
                  <tr
                    key={inq._id}
                    className={`border-b border-gray-50 transition-colors hover:bg-orange-50/40 dark:border-white/5 dark:hover:bg-[#1a1a1a] ${isFetching ? "opacity-60" : ""}`}
                  >
                    {/* User */}
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-500 text-xs font-bold text-white">
                          {inq.sName?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                        <div>
                          <p className="font-semibold text-navy-700 dark:text-white">{inq.sName}</p>
                          <p className="text-xs text-gray-500">{inq.sEmail}</p>
                        </div>
                      </div>
                    </td>

                    {/* Message preview */}
                    <td className="max-w-[200px] py-3 pr-4">
                      <p className="text-gray-600 dark:text-gray-300">
                        {inq.sMessage?.length > 55
                          ? inq.sMessage.slice(0, 55) + "…"
                          : inq.sMessage}
                      </p>
                      {inq.sAdminReply && (
                        <p className="mt-0.5 text-xs text-green-500 dark:text-green-400">
                          ✓ Replied
                        </p>
                      )}
                    </td>

                    {/* Status */}
                    <td className="py-3 pr-4">
                      <StatusBadge status={inq.eStatus} />
                    </td>

                    {/* Date */}
                    <td className="py-3 pr-4 text-xs text-gray-500">
                      {inq.dCreatedAt ? new Date(inq.dCreatedAt).toLocaleDateString() : "—"}
                    </td>

                    {/* Actions */}
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        {/* Reply button — always visible */}
                        <button
                          onClick={() => setSelectedInquiry(inq)}
                          className="flex items-center gap-1 rounded-lg bg-orange-500 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-orange-600 transition-colors"
                          title="View & Reply"
                        >
                          <MdReply className="h-3.5 w-3.5" />
                          {inq.sAdminReply ? "View" : "Reply"}
                        </button>

                        {/* Status toggle */}
                        {inq.eStatus === "Pending" ? (
                          <button
                            disabled={updating}
                            onClick={() => doUpdateStatus({ id: inq._id, eStatus: "Closed" })}
                            className="flex items-center gap-1 rounded-lg bg-green-500 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-green-600 disabled:opacity-60 transition-colors"
                            title="Close without reply"
                          >
                            <MdCheckCircle className="h-3.5 w-3.5" />
                            Close
                          </button>
                        ) : (
                          <button
                            disabled={updating}
                            onClick={() => doUpdateStatus({ id: inq._id, eStatus: "Pending" })}
                            className="flex items-center gap-1 rounded-lg border border-orange-300 px-2.5 py-1.5 text-xs font-semibold text-orange-500 hover:bg-orange-50 disabled:opacity-60 transition-colors dark:border-orange-500/30 dark:hover:bg-orange-500/10"
                            title="Reopen inquiry"
                          >
                            <MdAccessTime className="h-3.5 w-3.5" />
                            Reopen
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {nTotalPages > 1 && (
          <div className="mt-5 flex items-center justify-between">
            <p className="text-xs text-gray-500">Page {nPage} of {nTotalPages}</p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={nPage === 1}
                className="rounded-lg border border-orange-200 px-3 py-1.5 text-xs font-medium text-orange-600 hover:bg-orange-50 disabled:opacity-40 dark:border-white/10 dark:text-gray-400 dark:hover:bg-[#1a1a1a]"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(nTotalPages, p + 1))}
                disabled={nPage === nTotalPages}
                className="rounded-lg border border-orange-200 px-3 py-1.5 text-xs font-medium text-orange-600 hover:bg-orange-50 disabled:opacity-40 dark:border-white/10 dark:text-gray-400 dark:hover:bg-[#1a1a1a]"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* Reply drawer */}
      {selectedInquiry && (
        <ReplyDrawer
          inquiry={selectedInquiry}
          onClose={() => setSelectedInquiry(null)}
          onReplied={handleReplied}
        />
      )}
    </div>
  );
};

export default ContactInquiries;
